import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Screen, TopBar, Logo, BackButton, ScrollArea, BottomBar, PrimaryButton, TextField, TextArea } from "@/components/jk/ui";
import { Card, Chip, Pill, SectionLabel } from "@/components/jk/kit";
import { ProgressBar } from "@/components/jk/kit";
import { formatINR, formatDate, formatTime } from "@/lib/format";
import api from "@/lib/api";

const QUERY_TYPES = ["Career", "Marriage", "Health", "Finance", "Education", "General"];

const ClientIntake = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();

  const sel = location.state || JSON.parse(sessionStorage.getItem(`jk_book_${slug}`) || "null");

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [tob, setTob] = useState("");
  const [pob, setPob] = useState("");
  const [por, setPor] = useState("");
  const [queryType, setQueryType] = useState("General");
  const [mode, setMode] = useState("");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState("");
  const [errField, setErrField] = useState("");
  const [saving, setSaving] = useState(false);

  const modes = sel?.service?.consultation_modes?.length ? sel.service.consultation_modes : ["Phone Call"];

  useEffect(() => {
    if (!sel) navigate(`/${slug}`, { replace: true });
    else if (!mode) setMode(sel.preselectedMode || modes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!sel) return null;

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const today = new Date().toISOString().slice(0, 10);

  const normaliseMode = (m) => {
    if (!m) return "phone";
    const map = {
      "Phone Call": "phone",
      "phone": "phone",
      "In Person": "in_person",
      "in_person": "in_person",
      "Video Call": "video",
      "video": "video"
    };
    return map[m] || m.toLowerCase().replace(" ", "_");
  };

  const submit = async () => {
    if (name.trim().length < 2) { setErrField("name"); return setError("Please enter your full name."); }
    if (mobile.replace(/\D/g, "").length !== 10) { setErrField("mobile"); return setError("Mobile number must be 10 digits."); }
    if (!validEmail(email)) { setErrField("email"); return setError("Please enter a valid email."); }
    if (dob && dob >= today) { setErrField("dob"); return setError("Date of birth must be in the past."); }
    if (pob.trim() && pob.trim().length < 2) { setErrField("pob"); return setError("Please enter a valid place of birth."); }
    setError(""); setErrField("");
    setSaving(true);
    try {
      const { data } = await api.post(`/public/astrologer/${slug}/book`, {
        service_id: sel.serviceId, booking_date: sel.date, booking_time: sel.time,
        consultation_mode: normaliseMode(mode), tier: sel.tier ?? 1,
        full_name: name.trim(), mobile_number: mobile.trim(),
        mobile_country_code: "+91", email: email.trim().toLowerCase(),
        date_of_birth: dob, time_of_birth: tob, place_of_birth: pob.trim(),
        place_of_residence: por.trim(),
        query_type: queryType, query_detail: detail.trim(),
      });
      sessionStorage.removeItem(`jk_book_${slug}`);
      if (data.status === "paywall") {
        navigate(`/booking/${data.booking_id}/done`, { replace: true });
      } else {
        navigate(`/${slug}/payment`, { state: { bookingId: data.booking_id, amount: data.amount, serviceName: data.service_name, sel } });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create booking.");
      setSaving(false);
    }
  };

  return (
    <Screen testid="intake-screen">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<Logo className="h-[16px] w-auto" />} />
      <div className="px-6 pt-1"><ProgressBar value={50} /><p className="mt-2 text-[12px] text-[#666666]">Step 1 of 2</p></div>

      <ScrollArea className="px-6 pt-4">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-[#111111]">Your details</h1>

        <Card className="mt-4" data-testid="booking-summary">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[15px] font-semibold text-[#111111]">{sel.service.name}</p>
              <p className="mt-0.5 text-[12px] text-[#666666]">{formatDate(sel.date)} · {formatTime(sel.time)}</p>
            </div>
            <Pill tone="periwinkle">{sel.displayedPrice || formatINR(sel.service.price_inr)}</Pill>
          </div>
        </Card>

        <div className="mt-6 space-y-5 pb-4">
          <TextField testid="intake-name" label="Full name" placeholder="Your name" value={name} error={errField === "name" ? error : ""} onChange={(e) => { setName(e.target.value); setError(""); setErrField(""); }} />
          <TextField testid="intake-mobile" label="Mobile number" inputMode="numeric" maxLength={10} prefix="+91" placeholder="98765 43210" value={mobile} error={errField === "mobile" ? error : ""} onChange={(e) => { setMobile(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); setErrField(""); }} />
          <TextField testid="intake-email" label="Email" type="email" placeholder="your@email.com" value={email} error={errField === "email" ? error : ""} onChange={(e) => { setEmail(e.target.value); setError(""); setErrField(""); }} />

          <div>
            <SectionLabel className="mb-3">Birth details (for your reading)</SectionLabel>
            <div className="space-y-4">
              <TextField testid="intake-dob" label="Date of birth" type="date" max={today} value={dob} error={errField === "dob" ? error : ""} onChange={(e) => { setDob(e.target.value); setError(""); setErrField(""); }} />
              <TextField testid="intake-tob" label="Time of birth" type="time" value={tob} onChange={(e) => setTob(e.target.value)} />
              <TextField testid="intake-pob" label="Place of birth" placeholder="City, State" value={pob} error={errField === "pob" ? error : ""} onChange={(e) => { setPob(e.target.value); setError(""); setErrField(""); }} />
              <TextField testid="intake-por" label="Place of residence" placeholder="City where you currently live" value={por} onChange={(e) => { setPor(e.target.value); setError(""); setErrField(""); }} />
            </div>
          </div>

          <div>
            <SectionLabel className="mb-3">What&apos;s your query about?</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {QUERY_TYPES.map((q) => <Chip key={q} selected={queryType === q} onClick={() => setQueryType(q)} testid={`query-${q.toLowerCase()}`}>{q}</Chip>)}
            </div>
          </div>

          {modes.length > 1 && (
            <div>
              <SectionLabel className="mb-3">Consultation mode</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {modes.map((m) => <Chip key={m} selected={mode === m} onClick={() => setMode(m)} testid={`mode-${m.replace(/\s+/g, "-").toLowerCase()}`}>{m}</Chip>)}
              </div>
            </div>
          )}

          <TextArea testid="intake-detail" label="Tell the astrologer more (optional)" rows={3} maxLength={500} placeholder="Share any context about your question…" value={detail} onChange={(e) => setDetail(e.target.value)} />
        </div>
      </ScrollArea>

      <BottomBar>
        {error && <p className="mb-3 text-center text-[13px] text-[#d23b2f]" data-testid="intake-error">{error}</p>}
        <PrimaryButton testid="intake-continue" onClick={submit} disabled={saving} icon={<ArrowRight size={18} strokeWidth={2} />}>
          {saving ? "Please wait…" : "Continue to Payment"}
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default ClientIntake;
